import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { CtaFooter } from '../../components/ai/CtaFooter';
import { ServiceCard, ServiceCardProps } from '../../components/ai/ServiceCard';
import { getCMSPage, CMSPageContent } from '../../services/api';

/**
 * intrnauts.ai Services — horizontal-stack shape per sketch 002 Variant B.
 *
 * Hardcoded studio identity (per CONTEXT.md):
 * - The three tier KEYS (Consulting / Development / Architecture)
 * - Card headlines, tags, spec grids (engagement row enforces the no-pricing voice rule)
 * - The four-step process timeline
 *
 * CMS-overridable via `services` page:
 * - Scalar `headline` → masthead h1
 * - Scalar `intro` → masthead lede
 * - Block section `services`: block.fields.title overrides the tier-name word
 *   in the mono numeral; block.fields.body overrides the card description.
 */

interface TierTemplate {
  key: 'consulting' | 'development' | 'architecture';
  tier: string;
  headline: string;
  description: string;
  tags: string[];
  spec: { k: string; v: string }[];
}

const TIERS: TierTemplate[] = [
  {
    key: 'consulting',
    tier: 'Consulting',
    headline: 'Clarity, in weeks not quarters.',
    description: 'Short engagements that answer a single hard question: can this AI thing work? You get a defensible recommendation and a reference eval, not a deck.',
    tags: ['Evals', 'Tech selection', 'Build-vs-buy'],
    spec: [
      { k: 'Cycle',      v: '2–8 weeks' },
      { k: 'Output',     v: 'Doc + eval set' },
      { k: 'Format',     v: 'Async + 2 syncs/wk' },
      { k: 'Engagement', v: 'By scope' },
    ],
  },
  {
    key: 'development',
    tier: 'Development',
    headline: 'Build the product. Ship it. Hand it back.',
    description: 'Small team, weekly demos, no agency layers. We do the front-end, the back-end, the AI surface, the evals, the observability — you own it from day one.',
    tags: ['Full-stack', 'AI features', 'Production'],
    spec: [
      { k: 'Cycle',      v: '2–6 months' },
      { k: 'Output',     v: 'Shipped + runbook' },
      { k: 'Format',     v: 'Daily merges' },
      { k: 'Engagement', v: 'By scope' },
    ],
  },
  {
    key: 'architecture',
    tier: 'Architecture',
    headline: 'The senior call, fractionally.',
    description: 'Ongoing technical leadership for teams that need the senior judgement without the senior salary — architecture review, pattern design, sitting in on the hard meetings.',
    tags: ['Tech leadership', 'System design', 'Hiring'],
    spec: [
      { k: 'Cycle',      v: 'Quarterly' },
      { k: 'Output',     v: 'Decisions + docs' },
      { k: 'Format',     v: '2–3 days/wk' },
      { k: 'Engagement', v: 'By scope' },
    ],
  },
];

const PROCESS_STEPS = [
  { label: '// 01 — Frame',    title: 'Find the real question.',                 body: 'The brief is rarely the problem. First week is spent finding the question worth answering.' },
  { label: '// 02 — Eval',     title: 'Define what good looks like.',            body: "Before code, we agree on what we'd measure to know we'd won. Rubrics, reference sets, regression diffs." },
  { label: '// 03 — Build',    title: 'Smallest thing that resolves the eval.',  body: 'Weekly demos against the rubric. We ship the smallest system that moves the score, then iterate.' },
  { label: '// 04 — Hand off', title: 'Code, runbook, regret list.',             body: "You get the system, the docs, and an honest list of what we'd change next quarter." },
];

const ServicesAi: React.FC = () => {
  const [content, setContent] = useState<CMSPageContent | null>(null);

  useEffect(() => {
    getCMSPage('services').then(res => {
      if (res.data) setContent(res.data);
    });
  }, []);

  const fields = content?.fields ?? {};
  const cmsBlocks = content?.blocks?.services ?? [];

  const cards: ServiceCardProps[] = TIERS.map((t, idx) => {
    const cmsBlock = cmsBlocks[idx];
    const tier = cmsBlock?.fields?.title?.trim() || t.tier;
    const description = cmsBlock?.fields?.body?.trim() || t.description;
    return {
      num: `// 0${idx + 1} — ${tier}`,
      headline: t.headline,
      description,
      tags: t.tags,
      spec: t.spec,
    };
  });

  return (
    <>
      <section className="ai-services-masthead">
        <div className="eyebrow">● Services — how we engage</div>
        <h1 className="ai-services-h1">
          {fields.headline ? (
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fields.headline) }} />
          ) : (
            <>
              Three modes.<br />
              <i>One discipline.</i>
            </>
          )}
        </h1>
        <p className="ai-services-intro">
          {fields.intro ?? 'Engagements scaled to the shape of the problem — quick clarity work, full builds, or ongoing technical partnership. Same evals-first, observability-included, no-vapor approach across all three.'}
        </p>
      </section>

      <div className="ai-scroll-hint" aria-hidden="true">
        <span>scroll →</span>
        <span className="rule" />
        <span>{`0${cards.length} services`}</span>
      </div>

      <section className="ai-stack" aria-label="Service tiers">
        {cards.map(c => (
          <ServiceCard key={c.num} {...c} />
        ))}
      </section>

      <section className="ai-process" aria-labelledby="ai-process-head">
        <h2 id="ai-process-head" className="ai-process-head">
          However we engage, the <i>shape</i> is the same.
        </h2>
        <div className="ai-timeline">
          {PROCESS_STEPS.map(s => (
            <div key={s.label} className="ai-step">
              <div className="ai-step-label">{s.label}</div>
              <h4 className="ai-step-h4">{s.title}</h4>
              <p className="ai-step-body">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <CtaFooter
        headline={<>Not sure which mode <i>fits?</i><br />Tell us about it.</>}
        ctaLabel="Book intro call"
        ctaTo="/contact"
      />
    </>
  );
};

export default ServicesAi;
